// constants/permissions.js

/**
 * RBAC Permissions Model
 * -------------------------------------------------

 */

// Permission metadata coming from backend (converted into frontend-friendly exports)
export const AVAILABLE_PERMISSIONS = [
  {
    name: "can_manage_users",
    description: "Can manage users and invitations",
    category: "user_management",
  },
  {
    name: "can_create_projects",
    description: "Can create and manage projects",
    category: "project_management",
  },
  {
    name: "can_manage_budgets",
    description: "Can manage budgets and financial data",
    category: "financial",
  },
  {
    name: "can_delete_expenses",
    description: "Can delete expense records",
    category: "financial",
  },
  {
    name: "can_view_all_projects",
    description: "Can view all project data",
    category: "project_management",
  },
];

export const PERMISSION_CATEGORIES = [
  "user_management",
  "project_management",
  "financial",
];

// Role -> permission keys mapping as returned from backend. Some backends prefix
// permissions (e.g. "users.can_manage_users"). Normalize to raw permission names.
const RAW_ROLE_PERMISSIONS = {
  admin: [
    "users.can_manage_users",
    "users.can_create_projects",
    "users.can_manage_budgets",
    "users.can_delete_expenses",
    "users.can_view_all_projects",
  ],
  project_manager: ["users.can_create_projects", "users.can_view_all_projects"],
  staff: [],
  finance_officer: ["users.can_manage_budgets", "users.can_delete_expenses"],
};

// Generate an easy-to-use PERMISSIONS constant (e.g. PERMISSIONS.CAN_MANAGE_USERS)
export const PERMISSIONS = AVAILABLE_PERMISSIONS.reduce((acc, p) => {
  const key = p.name
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^(?:_+|_+)$/g, "");

  acc[key] = p.name;
  return acc;
}, {});

/**
 * Convenience: return list of permission keys for a given role.
 * e.g. getRolePermissions('admin') -> ['can_manage_users', ...]
 */
const ROLE_PERMISSIONS_MAP = Object.fromEntries(
  Object.entries(RAW_ROLE_PERMISSIONS).map(([role, perms]) => [
    role,
    perms.map((p) => p.split(".").pop()), // removes prefix like "users."
  ])
);
export const getRolePermissions = (role) => ROLE_PERMISSIONS_MAP[role] || [];

export const ROLES = {
  SUPERADMIN: "superadmin",
  ADMIN: "admin",
  PROJECT_MANAGER: "project_manager",
  FINANCE_OFFICER: "finance_officer",
  STAFF: "staff",
};

//  Role groups (collections of GROUPS)
export const ROLE_GROUPS = {
  // Only admins
  ADMINS: [ROLES.ADMIN, ROLES.SUPERADMIN],

  // All staff (base role)
  STAFF: [ROLES.STAFF],

  // Staff and above (staff, PMs, finance, admins)
  STAFF_AND_ABOVE: [
    ROLES.STAFF,
    ROLES.PROJECT_MANAGER,
    ROLES.FINANCE_OFFICER,
    ROLES.ADMIN,
  ],
  PROJECT_MANAGERS: [ROLES.PROJECT_MANAGER],
  FINANCE_OFFICERS: [ROLES.FINANCE_OFFICER],

  // Managers and above (PMs, finance, admins)
  MANAGERS_AND_ABOVE: [
    ROLES.PROJECT_MANAGER,
    ROLES.FINANCE_OFFICER,
    ROLES.ADMIN,
  ],

  // Authenticated users (any logged-in role)
  AUTHENTICATED: [
    ROLES.STAFF,
    ROLES.PROJECT_MANAGER,
    ROLES.FINANCE_OFFICER,
    ROLES.ADMIN,
  ],
};
export default {
  PERMISSIONS,
  AVAILABLE_PERMISSIONS,
  PERMISSION_CATEGORIES,
  ROLE_PERMISSIONS_MAP,
  getRolePermissions,
  ROLES,
  ROLE_GROUPS,
};
